import hljs from "highlight.js/lib/core"
import bash from "highlight.js/lib/languages/bash"
import css from "highlight.js/lib/languages/css"
import go from "highlight.js/lib/languages/go"
import javascript from "highlight.js/lib/languages/javascript"
import json from "highlight.js/lib/languages/json"
import markdown from "highlight.js/lib/languages/markdown"
import python from "highlight.js/lib/languages/python"
import typescript from "highlight.js/lib/languages/typescript"
import xml from "highlight.js/lib/languages/xml"
import yaml from "highlight.js/lib/languages/yaml"

// Bug Inspect runs vitest, jest, pytest and go test, so these cover its diffs;
// registering them one by one keeps the rest of hljs out of the bundle.
const LANGUAGES = {
	bash,
	css,
	go,
	javascript,
	json,
	markdown,
	python,
	typescript,
	xml,
	yaml,
}
for (const [name, language] of Object.entries(LANGUAGES)) {
	if (!hljs.getLanguage(name)) hljs.registerLanguage(name, language)
}

const EXTENSIONS: Record<string, keyof typeof LANGUAGES> = {
	ts: "typescript",
	tsx: "typescript",
	mts: "typescript",
	cts: "typescript",
	js: "javascript",
	jsx: "javascript",
	mjs: "javascript",
	cjs: "javascript",
	py: "python",
	go: "go",
	json: "json",
	css: "css",
	scss: "css",
	html: "xml",
	xml: "xml",
	svg: "xml",
	vue: "xml",
	yml: "yaml",
	yaml: "yaml",
	md: "markdown",
	mdx: "markdown",
	sh: "bash",
}

export function languageForFile(filename: string): string | null {
	const extension = filename.split(".").pop()?.toLowerCase()
	return (extension && EXTENSIONS[extension]) || null
}

export type PatchLineKind = "added" | "removed" | "context" | "hunk" | "note"

export interface PatchLine {
	kind: PatchLineKind
	marker: string
	// Escaped HTML from hljs, or the escaped raw text when there is no language.
	html: string
}

const escapeHtml = (text: string) =>
	text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

function highlight(code: string, language: string | null): string {
	if (!language) return escapeHtml(code)
	try {
		return hljs.highlight(code, { language, ignoreIllegals: true }).value
	} catch {
		return escapeHtml(code)
	}
}

// Each line is highlighted on its own, so a string or comment spanning lines
// loses its colour past the first. A diff only shows fragments anyway.
export function parsePatch(patch: string, filename: string): PatchLine[] {
	const language = languageForFile(filename)
	return patch.split("\n").map((line): PatchLine => {
		if (line.startsWith("@@")) {
			return { kind: "hunk", marker: "", html: escapeHtml(line) }
		}
		if (line.startsWith("\\")) {
			return { kind: "note", marker: "", html: escapeHtml(line) }
		}
		const marker = line.charAt(0)
		const kind =
			marker === "+" ? "added" : marker === "-" ? "removed" : "context"
		return {
			kind,
			marker: kind === "context" ? " " : marker,
			html: highlight(line.slice(1), language),
		}
	})
}
